import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd, CheckCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { registerCustomer, clearRegistration } from '../store/slices/customerAuthSlice';
import { customerAPI } from '../services/customerAPI';
import PasswordStrengthIndicator, { isPasswordValid } from '../components/PasswordStrengthIndicator';
import { sanitizeText, sanitizeError } from '../utils/sanitize';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { registration } = useSelector((state: RootState) => state.customerAuth);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    acceptTerms: false,
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Clear registration state on mount
  useEffect(() => {
    dispatch(clearRegistration());
  }, [dispatch]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle blur for field touch tracking
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Validation functions
  const validateEmail = (email: string): string | null => {
    if (!email) return 'Email is required';
    if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const validatePassword = (password: string): string | null => {
    if (!password) return 'Password is required';
    if (!isPasswordValid(password)) return 'Password does not meet requirements';
    return null;
  };

  const validateConfirmPassword = (confirmPassword: string): string | null => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== formData.password) return 'Passwords do not match';
    return null;
  };

  const validateFirstName = (firstName: string): string | null => {
    if (!firstName.trim()) return 'First name is required';
    if (firstName.length > 100) return 'First name must be 100 characters or less';
    return null;
  };

  const validateLastName = (lastName: string): string | null => {
    if (!lastName.trim()) return 'Last name is required';
    if (lastName.length > 100) return 'Last name must be 100 characters or less';
    return null;
  };

  // Get field errors (including server validation errors)
  const getFieldError = (field: string): string | null => {
    // Server validation errors take priority
    if (registration.validationErrors[field]) {
      return registration.validationErrors[field];
    }
    // Only show client validation after field is touched
    if (!touched[field]) return null;

    switch (field) {
      case 'email':
        return validateEmail(formData.email);
      case 'password':
        return validatePassword(formData.password);
      case 'confirmPassword':
        return validateConfirmPassword(formData.confirmPassword);
      case 'firstName':
        return validateFirstName(formData.firstName);
      case 'lastName':
        return validateLastName(formData.lastName);
      default:
        return null;
    }
  };

  // Check if form is valid
  const isFormValid = (): boolean => {
    return (
      !validateEmail(formData.email) &&
      !validatePassword(formData.password) &&
      !validateConfirmPassword(formData.confirmPassword) &&
      !validateFirstName(formData.firstName) &&
      !validateLastName(formData.lastName) &&
      formData.acceptTerms
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      firstName: true,
      lastName: true,
    });

    if (!isFormValid()) {
      return;
    }

    await dispatch(
      registerCustomer({
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        accept_terms: formData.acceptTerms,
      })
    );
  };

  // Success state - show confirmation message
  if (registration.success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Registration Successful!
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            We've sent a verification email to <strong>{sanitizeText(formData.email)}</strong>.
            Please check your inbox and click the verification link to activate your account.
          </Typography>
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Didn't receive the email? Check your spam folder or{' '}
              <Link
                component="button"
                variant="body2"
                disabled={resendLoading}
                onClick={async () => {
                  setResendLoading(true);
                  setResendMessage('');
                  try {
                    await customerAPI.resendVerification(formData.email);
                    setResendMessage('Verification email sent! Check your inbox.');
                  } catch {
                    setResendMessage('Failed to resend. Please try again later.');
                  } finally {
                    setResendLoading(false);
                  }
                }}
              >
                click here to resend
              </Link>
              .
            </Typography>
            {resendMessage && (
              <Typography variant="body2" sx={{ mt: 1 }} color={resendMessage.includes('sent') ? 'success.main' : 'error.main'}>
                {resendMessage}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 4 }}
            component={RouterLink}
            to="/customer/login"
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <PersonAdd sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            Create Account
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Sign up to track your support cases and uploads
          </Typography>
        </Box>

        {registration.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {sanitizeError(registration.error)}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name fields in a row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              onBlur={() => handleBlur('firstName')}
              error={!!getFieldError('firstName')}
              helperText={getFieldError('firstName')}
              margin="normal"
              required
              autoComplete="given-name"
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              onBlur={() => handleBlur('lastName')}
              error={!!getFieldError('lastName')}
              helperText={getFieldError('lastName')}
              margin="normal"
              required
              autoComplete="family-name"
            />
          </Box>

          {/* Email */}
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={() => handleBlur('email')}
            error={!!getFieldError('email')}
            helperText={getFieldError('email')}
            margin="normal"
            required
            autoComplete="email"
          />

          {/* Password */}
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            onBlur={() => handleBlur('password')}
            error={!!getFieldError('password')}
            helperText={getFieldError('password')}
            margin="normal"
            required
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Password strength indicator */}
          <PasswordStrengthIndicator password={formData.password} />

          {/* Confirm Password */}
          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            onBlur={() => handleBlur('confirmPassword')}
            error={!!getFieldError('confirmPassword')}
            helperText={getFieldError('confirmPassword')}
            margin="normal"
            required
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Terms checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link href="/terms" target="_blank" rel="noopener">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link href="/privacy" target="_blank" rel="noopener">
                  Privacy Policy
                </Link>
              </Typography>
            }
            sx={{ mt: 2 }}
          />

          {/* Submit button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={registration.loading || !formData.acceptTerms}
            sx={{ mt: 3, mb: 2 }}
          >
            {registration.loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>

          {/* Login link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Already have an account?{' '}
              <Link component={RouterLink} to="/customer/login">
                Sign in
              </Link>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default RegisterPage;
